// Mirror of the API sentinel registry — api/src/lib/evie/sentinel/signals.ts
// (SENTINEL_CATEGORIES, SENTINEL_CATEGORY_LABELS, SENTINEL_CATEGORY_LANES,
// SENTINEL_LANE_RANK). The web tree cannot import api code: update the two
// files in lockstep. Heavy-ops retaxonomy P0 (docs/proposals/2026/09/
// 2026-09-04-evie-sentinel-heavy-ops-retaxonomy.md §1): operator-language
// labels over the CURRENT slugs — the slug rename migration is P1. Raw slugs
// stay in tooltips and data-testids for greppability.

export const SENTINEL_CATEGORIES = [
  'stalled',
  'spam_loop',
  'confused_applicant',
  'unanswered_applicant',
  'wrong_info',
  'doc_trouble',
  'writeback_stuck',
  'writeback_held',
  'parked_report',
  'closure_divergence',
  'wrongful_timeout_risk',
  'process_error',
  'quality',
  'other',
] as const
export type SentinelCategory = (typeof SENTINEL_CATEGORIES)[number]

/** Whose-move lanes; 'their_move' arrives with the P1 stalled split. */
export type SentinelLane =
  | 'applicant_waiting_on_us'
  | 'decision_at_risk'
  | 'staffs_desk'
  | 'their_move'
  | 'plumbing'

/** Worklist band order — applicant_waiting_on_us is the hottest lane. */
export const SENTINEL_LANE_RANK: Record<SentinelLane, number> = {
  applicant_waiting_on_us: 0,
  decision_at_risk: 1,
  staffs_desk: 2,
  their_move: 3,
  plumbing: 4,
}

// Rows with no open finding, and any lane this build does not know, sort
// after every mapped lane (the server uses the same shared last-place rank).
const UNRANKED_LANE_RANK = 99

export const SENTINEL_CATEGORY_LANES: Record<SentinelCategory, SentinelLane> = {
  unanswered_applicant: 'applicant_waiting_on_us',
  confused_applicant: 'applicant_waiting_on_us',
  spam_loop: 'applicant_waiting_on_us',
  doc_trouble: 'applicant_waiting_on_us',
  process_error: 'applicant_waiting_on_us',
  stalled: 'applicant_waiting_on_us',
  wrong_info: 'applicant_waiting_on_us',
  wrongful_timeout_risk: 'decision_at_risk',
  writeback_stuck: 'decision_at_risk',
  writeback_held: 'decision_at_risk',
  parked_report: 'staffs_desk',
  closure_divergence: 'plumbing',
  quality: 'plumbing',
  other: 'plumbing',
}

export const SENTINEL_CATEGORY_LABELS: Record<SentinelCategory, string> = {
  stalled: 'Going nowhere',
  confused_applicant: 'Needs guidance',
  unanswered_applicant: 'Left hanging',
  wrongful_timeout_risk: 'Unfair timeout',
  spam_loop: 'Getting blasted',
  quality: 'Flip-flopping',
  doc_trouble: 'Docs bouncing',
  process_error: 'Dropped the ball',
  closure_divergence: 'Zombie case',
  writeback_stuck: 'Result never delivered',
  writeback_held: 'Result held up',
  parked_report: 'Report parked',
  wrong_info: 'Talking nonsense',
  other: 'Other',
}

/**
 * The paging allowlist — also the worklist cutline exemption set: a
 * page-grade finding never ages out of view (mirror of
 * SENTINEL_PAGE_CATEGORIES in signals.ts).
 */
export const SENTINEL_PAGE_CATEGORIES = [
  'spam_loop',
  'wrongful_timeout_risk',
  'unanswered_applicant',
  'writeback_stuck',
] as const

export const isSentinelPageCategory = (
  category: string | null | undefined
): boolean =>
  category != null &&
  (SENTINEL_PAGE_CATEGORIES as readonly string[]).includes(category)

/** Display label for a slug; an unknown slug falls back to itself. */
export const sentinelCategoryLabel = (
  category: string | null | undefined
): string | null => {
  if (category == null) return null
  const labels: Partial<Record<string, string>> = SENTINEL_CATEGORY_LABELS
  return labels[category] ?? category
}

export const sentinelCategoryLane = (
  category: string | null | undefined
): SentinelLane | null => {
  if (category == null) return null
  const lanes: Partial<Record<string, SentinelLane>> = SENTINEL_CATEGORY_LANES
  return lanes[category] ?? null
}

export const sentinelLaneRank = (lane: string | null | undefined): number => {
  if (lane == null) return UNRANKED_LANE_RANK
  const ranks: Partial<Record<string, number>> = SENTINEL_LANE_RANK
  return ranks[lane] ?? UNRANKED_LANE_RANK
}
